import { DataProvider, GlobalActionsProvider } from '@plasmicapp/host';
import { MainDrawDay } from 'constants/Draw';
import { useABTestStore } from 'hooks/stores/useABTestStore';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import { convertMicroCostToLegacy } from 'utils/cost/convertMicroCost';
import { ticketContainsDuplicateLines } from 'utils/TicketValidation';

interface EnterFlowGlobalContextProps {
  // You might use this to override the auth URL to a test or local URL.
}

export const EnterFlowGlobalContext = observer(
  ({ children }: React.PropsWithChildren) => {
    const { abTests } = useABTestStore();

    const {
      entryMethod,
      num_entries,
      lines,
      days,
      boosted,
      discountFirstEntry,
      discountBannerText,
      currentPromo,
      promoCodeMessage,
      promoErrorMessage,
      orderError,
      orderId,
      isValid,
      isReferralFlow,
      orderCost,
      plan,
      goUpgradeOffer,
      setEntryMethod,
      setNumEntries,
      setLines,
      removeLine,
      setLineNumber,
      setDays,
      toggleDay,
      setBoosted,
      applyPromoCode: setPromoCode,
      toggleLineNumber,
      toggleLineBonusNumber,
      generateLineRandomNumbers,
      chooseNumbers,
      navigateToEnterTest,
      navigateToCheckout,
      setErrorPopupMessage,
      promoCodeApplicationResult,
    } = useEnterStore();

    // Adapt setDays to accept a string instead of an array
    const setDaysString = (dayString: string) => {
      if (dayString === 'BOTH') {
        setDays([MainDrawDay.TUESDAY, MainDrawDay.FRIDAY]);
      } else {
        setDays([dayString as MainDrawDay]);
      }
    };

    const areEntriesValidOrShowWarning = () => {
      const hasDuplicateLines = ticketContainsDuplicateLines(lines);
      if (hasDuplicateLines) {
        setErrorPopupMessage('Uh oh, looks like you are trying to create 2 entries with the same numbers, which isn\'t an option. Please change at least one of the numbers.');
        return false;
      }
      return true;
    }

    // Memoize actions to prevent unnecessary re-renders
    const actions = useMemo(
      () => ({
        setEntryMethod,
        setNumEntries,
        setLines,
        removeLine,
        setLineNumber,
        // Overwrite setDays to accept a string
        setDays: setDaysString,
        toggleDay,
        setBoosted,
        setPromoCode,
        toggleLineNumber,
        toggleLineBonusNumber,
        generateLineRandomNumbers,
        goUpgradeOffer,
        chooseNumbers,
        navigateToEnterTest,
        navigateToCheckout,
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        setEntryMethod,
        setNumEntries,
        setLines,
        removeLine,
        setLineNumber,
        setDays, // still depends on the original setDays
        toggleDay,
        setBoosted,
        setPromoCode,
        toggleLineNumber,
        toggleLineBonusNumber,
        generateLineRandomNumbers,
        chooseNumbers,
        navigateToEnterTest,
        navigateToCheckout,
      ]
    );

    // Prepare the data to be provided to the context
    const data = {
      abTests,
      entryMethod,
      num_entries,
      lines: lines.map((line) => ({
        main: line.main,
        bonus: line.bonus[0],
      })),
      days: days.length > 1 ? 'BOTH' : days[0],
      boosted,
      promoCode: currentPromo?.code || null,
      /** We have to mimic the behaviour of EnterGlobalContext as used by Plasmic before DRIP */
      promoCodeApplicationResult,
      promoCodeMessage,
      /** We have to mimic the behaviour of EnterGlobalContext as used by Plasmic before DRIP */
      promoCodeMessages: promoCodeMessage ? [promoCodeMessage] : [],
      promoErrorMessage,
      orderError,
      orderId,
      discountBannerText,
      regularOrderCost: convertMicroCostToLegacy(orderCost.price),
      payNowCost: convertMicroCostToLegacy(orderCost.cost),
      isValid,
      isReferralFlow,
      entryMethodBundles: plan
        ? Object.entries(plan.priceTable[0].GBP).map(
            ([min_number_entries, price_per_entry]) => ({
              min_number_entries: Number(min_number_entries),
              price_per_entry: convertMicroCostToLegacy(price_per_entry),
            }),
          )
        : [],
      plan,
      areEntriesValidOrShowWarning,

      // In DRIP forceBoostWording should be true. This comment will hopefully make a merge conflict obvious
      forceBoostWording: true, // In DRIP forceBoostWording should be true
      // In DRIP forceBoostWording should be true
    };

    return (
      <GlobalActionsProvider
        contextName="EnterFlowGlobalContext"
        actions={actions}
      >
        <DataProvider name="enterFlow" data={data}>
          {children}
        </DataProvider>
      </GlobalActionsProvider>
    );
  }
);
